import React, { useEffect, useState } from "react";
import { Loader2, Plus, X, Save, Trash2, MapPin, Edit2, ChevronUp, ChevronDown, Package } from "lucide-react";
import { toast } from "sonner";
import api from "./api";

/**
 * /console/delivery-routes , multi-stop delivery route planner.
 *
 * Lets admin pull console_orders (with an address on file) into a route as
 * stops, reorder them, tick them off as delivered, and open the whole run in
 * Google Maps with one tap.
 */

const MAPS_BATCH = 10; // Google Maps directions accept ~10 points per link

/** Address fields only , never names or phone numbers. Returns [{label, url}] batches in saved stop order. */
export function buildMapsBatches(stops) {
  const addrs = (stops || []).filter((s) => s.status !== "done").map((s) => sanitizeAddress(s.address)).filter(Boolean);
  if (addrs.length === 0) return [];
  const out = [];
  for (let i = 0; i < addrs.length; i += MAPS_BATCH) {
    const chunk = addrs.slice(i, i + MAPS_BATCH).map(encodeURIComponent);
    const url = chunk.length === 1 ? `https://www.google.com/maps/search/?api=1&query=${chunk[0]}` : `https://www.google.com/maps/dir/${chunk.join("/")}`;
    out.push({ label: addrs.length > MAPS_BATCH ? `Stops ${i + 1}–${Math.min(i + MAPS_BATCH, addrs.length)}` : "Open in Google Maps", url });
  }
  return out;
}

/** Strips phone numbers / emails from legacy free-text addresses so contact details never reach the map query. */
export function sanitizeAddress(a) {
  return String(a || "").replace(/\S+@\S+/g, "").replace(/(\+?\d[\d\s-]{6,}\d)/g, "").replace(/\s{2,}/g, " ").replace(/^[\s,·-]+|[\s,·-]+$/g, "").trim();
}
const buildMapsUrl = (stops) => buildMapsBatches(stops)[0]?.url || null;

function formatAddress(addr) {
  if (!addr) return "";
  if (typeof addr === "string") return addr;
  return [addr.line1, addr.line2, addr.suburb, addr.city, addr.postcode].filter(Boolean).join(", ");
}

export default function ConsoleDeliveryRoutes() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [eligibleOrders, setEligibleOrders] = useState([]);
  const [showPicker, setShowPicker] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/console/delivery-routes");
      setRows(r.data || []);
    } catch { toast.error("Couldn't load routes."); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Load console orders into picker when editor opens
  useEffect(() => {
    if (!editing) { setShowPicker(false); return; }
    (async () => {
      try {
        const r = await api.get("/admin/delivery");
        setEligibleOrders((r.data?.orders || []).filter((o) => o.delivery_status !== "delivered"));
      } catch { setEligibleOrders([]); }
    })();
  }, [editing]);

  const blank = () => ({ id: null, name: "", region: "", notes: "", stops: [], active: true, scheduled_for: "" });

  const save = async () => {
    if (!editing.name.trim()) { toast.error("Name required."); return; }
    const payload = {
      name: editing.name.trim(),
      region: editing.region || "",
      notes: editing.notes || "",
      stops: editing.stops || [],
      active: !!editing.active,
      scheduled_for: editing.scheduled_for || null,
    };
    try {
      if (editing.id) await api.patch(`/console/delivery-routes/${editing.id}`, payload);
      else await api.post("/console/delivery-routes", payload);
      toast.success("Route saved.");
      setEditing(null);
      await load();
    } catch { toast.error("Save failed."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this route?")) return;
    try { await api.delete(`/console/delivery-routes/${id}`); await load(); toast.success("Deleted."); }
    catch { toast.error("Delete failed."); }
  };

  const updateStop = (idx, patch) => {
    const next = [...(editing.stops || [])];
    next[idx] = { ...next[idx], ...patch };
    setEditing({ ...editing, stops: next });
  };
  const removeStop = (idx) => {
    const next = [...(editing.stops || [])];
    next.splice(idx, 1);
    setEditing({ ...editing, stops: next });
  };
  const addStop = () => setEditing({ ...editing, stops: [...(editing.stops || []), { label: "", address: "", status: "pending" }] });
  const moveStop = (idx, dir) => {
    const next = [...(editing.stops || [])];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setEditing({ ...editing, stops: next });
  };
  const addOrderAsStop = (o) => {
    const addr = sanitizeAddress(o.address || formatAddress(o.address));
    if (!addr) { toast.error("That order has no delivery address , fix it on the order first."); return; }
    if ((editing.stops || []).some((s) => s.order_id === o.id)) { toast.info("Already on this route."); return; }
    setEditing({
      ...editing,
      stops: [...(editing.stops || []), { label: `${o.customer_name || "Customer"} · #${o.order_number || (o.id || "").slice(0, 8)}`, address: addr, status: "pending", order_id: o.id, source: "delivery-queue" }],
    });
    toast.success("Stop added.");
  };

  /** Marking a stop Done completes ONLY that order (explicit delivered evidence). Repeated clicks are no-ops. */
  const completeStop = async (route, idx) => {
    const stop = route.stops[idx];
    if (!stop || stop.status === "done") return;
    const prevStops = route.stops;
    const stops = route.stops.map((s, i) => (i === idx ? { ...s, status: "done", done_at: new Date().toISOString() } : s));
    try {
      if (stop.order_id) await api.post(`/admin/delivery/${stop.order_id}/status`, { status: "delivered", note: `Route ${route.name}` });
      await api.patch(`/console/delivery-routes/${route.id}`, { ...route, stops });
      toast.success(stop.order_id ? "Delivered , order marked complete." : "Stop done.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't record that delivery , nothing changed.");
      void prevStops;
    }
  };
  const failStop = async (route, idx) => {
    const stop = route.stops[idx];
    const note = window.prompt("What happened? (goes back to the queue with this note)", "");
    if (note === null) return;
    const stops = route.stops.map((s, i) => (i === idx ? { ...s, status: "failed", note } : s));
    try {
      if (stop.order_id) await api.post(`/admin/delivery/${stop.order_id}/status`, { status: "failed", note });
      await api.patch(`/console/delivery-routes/${route.id}`, { ...route, stops });
      toast.success("Returned to the delivery queue with your note.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't record that."); }
  };

  return (
    <div data-testid="console-delivery-routes">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#6E1E3A] font-semibold">Heritage Dahlias · Console</div>
          <h1 className="font-serif text-4xl text-[#3C2A1E] mt-1">Delivery routes</h1>
          <p className="text-[#7A6A55] mt-2 max-w-2xl text-sm">
            Plan a run from the shared local delivery queue (orders explicitly added to local delivery), reorder the stops, save, then open the saved order in Google Maps. Map links carry addresses only.
          </p>
        </div>
        <button type="button" onClick={() => setEditing(blank())} data-testid="routes-new" className="inline-flex items-center gap-1.5 h-10 px-4 rounded bg-[#3E1F46] hover:bg-[#2C1531] text-white text-[10px] tracking-[0.25em] uppercase font-semibold">
          <Plus className="w-4 h-4" /> New route
        </button>
      </header>

      {loading ? (
        <div className="text-[#7A6A55] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-[#E8DFCB] rounded-md p-10 text-center">
          <MapPin className="w-10 h-10 text-[#6E1E3A] mx-auto mb-3" />
          <p className="text-[#7A6A55]">No delivery routes yet. Click &ldquo;New route&rdquo; to plan one.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const batches = buildMapsBatches(r.stops);
            return (
              <article key={r.id} data-testid={`routes-row-${r.id}`} className="bg-white border border-[#E8DFCB] rounded-md p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-serif text-2xl text-[#3C2A1E]">{r.name}</h3>
                      <span className={"px-2 py-0.5 rounded text-[10px] tracking-[0.25em] uppercase font-semibold " + (r.active ? "bg-[#2F4A2A] text-white" : "bg-[#E8DFCB] text-[#7A6A55]")}>
                        {r.active ? "Active" : "Inactive"}
                      </span>
                      <span className="px-2 py-0.5 rounded text-[10px] tracking-[0.25em] uppercase font-semibold bg-[#6E1E3A]/10 text-[#6E1E3A]">{(r.stops || []).length} stops</span>
                    </div>
                    {(r.region || r.scheduled_for) && (
                      <div className="text-xs text-[#7A6A55] mt-0.5">
                        {r.region}{r.region && r.scheduled_for ? " · " : ""}
                        {r.scheduled_for ? new Date(r.scheduled_for).toLocaleDateString("en-NZ") : ""}
                      </div>
                    )}
                    {r.notes && <p className="mt-2 text-sm text-[#3C2A1E] whitespace-pre-wrap">{r.notes}</p>}
                    {Array.isArray(r.stops) && r.stops.length > 0 && (
                      <ol className="mt-3 space-y-1.5">
                        {r.stops.map((s, i) => (
                          <li key={i} className="flex items-start gap-3 text-sm">
                            <span className={"shrink-0 w-6 h-6 rounded-full font-semibold grid place-items-center text-xs " + (s.status === "done" ? "bg-[#2F4A2A] text-white" : "bg-[#6E1E3A]/10 text-[#6E1E3A]")}>
                              {s.status === "done" ? "✓" : i + 1}
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className={"text-[#3C2A1E] " + (s.status === "done" ? "line-through opacity-60" : "")}>{s.label || "Stop"}{s.status === "failed" ? <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-[#A33030] font-semibold">Failed · back in queue</span> : null}</div>
                              {s.address && <div className="text-xs text-[#7A6A55] break-words">{s.address}</div>}
                              {s.note && <div className="text-xs text-[#A33030] italic">{s.note}</div>}
                            </div>
                            {s.status !== "done" && (
                              <div className="shrink-0 flex gap-1">
                                <button type="button" onClick={() => completeStop(r, i)} data-testid={`routes-stop-done-${r.id}-${i}`} className="h-7 px-2 rounded bg-[#2F4A2A] text-white text-[10px] tracking-[0.15em] uppercase font-semibold">Delivered</button>
                                <button type="button" onClick={() => failStop(r, i)} data-testid={`routes-stop-failed-${r.id}-${i}`} className="h-7 px-2 rounded border border-[#A33030] text-[#A33030] text-[10px] tracking-[0.15em] uppercase font-semibold">Couldn't deliver</button>
                              </div>
                            )}
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {batches.map((b, bi) => (
                      <a key={bi} href={b.url} target="_blank" rel="noreferrer noopener" data-testid={`routes-maps-${r.id}${bi ? "-" + bi : ""}`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded bg-[#1A73E8] hover:bg-[#0F5DC4] text-white text-xs tracking-[0.15em] uppercase font-semibold">
                        <MapPin className="w-3.5 h-3.5" /> {b.label}
                      </a>
                    ))}
                    <button type="button" onClick={() => setEditing({ ...r })} data-testid={`routes-edit-${r.id}`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded border border-[#E8DFCB] text-[#3C2A1E] text-xs tracking-[0.15em] uppercase font-semibold hover:bg-[#FBF6E9]"><Edit2 className="w-3.5 h-3.5" /> Edit</button>
                    <button type="button" onClick={() => remove(r.id)} data-testid={`routes-delete-${r.id}`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded bg-white border border-[#A33030] text-[#A33030] hover:bg-[#FFF4F0] text-xs tracking-[0.15em] uppercase font-semibold"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {editing && (
        <div data-testid="routes-editor" className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-[#F8F4EA] border-2 border-[#3E1F46] rounded-md max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 shadow-2xl space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-2xl text-[#3C2A1E]">{editing.id ? "Edit route" : "New route"}</h3>
              <button type="button" onClick={() => setEditing(null)} className="p-1"><X className="w-5 h-5" /></button>
            </div>
            <Field label="Route name *"><input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="routes-edit-name" className="ji" placeholder="e.g. Friday Auckland West" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Region"><input value={editing.region} onChange={(e) => setEditing({ ...editing, region: e.target.value })} className="ji" placeholder="Waikato" /></Field>
              <Field label="Scheduled for"><input type="date" value={editing.scheduled_for ? editing.scheduled_for.slice(0, 10) : ""} onChange={(e) => setEditing({ ...editing, scheduled_for: e.target.value })} className="ji" /></Field>
            </div>
            <Field label="Notes"><textarea value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} rows={2} className="ji" /></Field>

            <div>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#9C8B73] font-semibold">Stops (in order)</span>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setShowPicker((v) => !v)} data-testid="routes-toggle-picker" className="inline-flex items-center gap-1 h-8 px-3 rounded border border-[#6E1E3A] text-xs text-[#6E1E3A] hover:bg-[#6E1E3A]/10 font-semibold">
                    <Package className="w-3.5 h-3.5" /> {showPicker ? "Hide" : "Pull from"} local delivery queue
                  </button>
                  <button type="button" onClick={addStop} data-testid="routes-add-stop" className="inline-flex items-center gap-1 h-8 px-3 rounded border border-[#E8DFCB] text-xs hover:bg-[#FBF6E9]"><Plus className="w-3.5 h-3.5" /> Add blank</button>
                </div>
              </div>

              {showPicker && (
                <div data-testid="routes-picker" className="mt-3 bg-white border border-[#6E1E3A]/20 rounded p-3 max-h-56 overflow-y-auto">
                  {eligibleOrders.length === 0 ? (
                    <p className="text-xs text-[#7A6A55] italic">No orders in the local delivery queue. Use “Add to local delivery” on an order first , shipping-stage or Upper Hutt addresses alone don’t qualify.</p>
                  ) : (
                    <ul className="divide-y divide-[#F4ECD8]">
                      {eligibleOrders.map((o) => {
                        const addr = formatAddress(o.address);
                        const used = (editing.stops || []).some((s) => s.order_id === o.id);
                        return (
                          <li key={o.id} className="py-2 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-[#3C2A1E]">{o.customer_name || "Customer"} · #{(o.id || "").slice(0, 8)}</div>
                              <div className="text-xs text-[#7A6A55] truncate">{addr || "No address on file"}</div>
                            </div>
                            <button
                              type="button"
                              disabled={used || !addr}
                              onClick={() => addOrderAsStop(o)}
                              data-testid={`routes-pick-${o.id}`}
                              className={"shrink-0 inline-flex items-center gap-1 h-8 px-3 rounded text-xs font-semibold tracking-[0.1em] uppercase " + (used ? "bg-[#2F4A2A]/20 text-[#2F4A2A] cursor-default" : addr ? "bg-[#6E1E3A] hover:bg-[#5a1830] text-white" : "bg-[#E8DFCB] text-[#9C8B73] cursor-not-allowed")}
                            >
                              {used ? "Added" : addr ? "Add" : "No address"}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}

              <div className="mt-3 space-y-2">
                {(editing.stops || []).map((s, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`routes-stop-${idx}`}>
                    <span className="col-span-1 w-7 h-7 rounded-full bg-[#6E1E3A]/10 text-[#6E1E3A] grid place-items-center text-xs font-semibold">{idx + 1}</span>
                    <input value={s.label} placeholder="Label" onChange={(e) => updateStop(idx, { label: e.target.value })} className="col-span-3 ji" />
                    <input value={s.address} placeholder="Address" onChange={(e) => updateStop(idx, { address: e.target.value })} className="col-span-4 ji" />
                    <span data-testid={`routes-stop-status-${idx}`} className="col-span-2 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#7A6A55] truncate">{s.status === "done" ? "Delivered" : s.status === "failed" ? "Failed" : "Pending"}</span>
                    <div className="col-span-2 flex items-center justify-end gap-0.5">
                      <button type="button" title="Move up" aria-label="Move up" data-testid={`routes-stop-up-${idx}`} onClick={() => moveStop(idx, -1)} disabled={idx === 0} className="h-7 px-1.5 rounded inline-flex items-center gap-0.5 text-[10px] text-[#7A6A55] hover:bg-[#FBF6E9] disabled:opacity-30"><ChevronUp className="w-4 h-4" /><span className="hidden xl:inline">Up</span></button>
                      <button type="button" title="Move down" aria-label="Move down" data-testid={`routes-stop-down-${idx}`} onClick={() => moveStop(idx, 1)} disabled={idx === (editing.stops || []).length - 1} className="h-7 px-1.5 rounded inline-flex items-center gap-0.5 text-[10px] text-[#7A6A55] hover:bg-[#FBF6E9] disabled:opacity-30"><ChevronDown className="w-4 h-4" /><span className="hidden xl:inline">Down</span></button>
                      <button type="button" title="Remove stop" aria-label="Remove stop" data-testid={`routes-stop-remove-${idx}`} onClick={() => removeStop(idx)} className="h-7 px-1.5 rounded inline-flex items-center gap-0.5 text-[10px] text-[#A33030] hover:bg-[#FFF4F0]"><X className="w-4 h-4" /><span className="hidden xl:inline">Remove</span></button>
                    </div>
                  </div>
                ))}
                {(editing.stops || []).length === 0 && <div className="text-xs text-[#7A6A55] italic">No stops yet.</div>}
              </div>

              {(editing.stops || []).length >= 2 && buildMapsUrl(editing.stops) && (
                <a href={buildMapsUrl(editing.stops)} target="_blank" rel="noreferrer noopener" data-testid="routes-preview-maps" className="mt-3 inline-flex items-center gap-1.5 h-9 px-3 rounded bg-[#1A73E8]/15 hover:bg-[#1A73E8]/25 text-[#1A73E8] text-xs tracking-[0.15em] uppercase font-semibold">
                  <MapPin className="w-3.5 h-3.5" /> Preview in Google Maps (addresses only)
                </a>
              )}
            </div>

            <label className="inline-flex items-center gap-2 text-sm text-[#3C2A1E]">
              <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active route
            </label>

            <div className="flex justify-between pt-2">
              {editing.id ? (
                <button type="button" onClick={() => remove(editing.id)} className="inline-flex items-center gap-1.5 h-10 px-4 rounded bg-white border border-[#A33030] text-[#A33030] text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[#FFF4F0]"><Trash2 className="w-4 h-4" /> Delete</button>
              ) : <span />}
              <button type="button" onClick={save} data-testid="routes-edit-save" className="inline-flex items-center gap-1.5 h-10 px-5 rounded bg-[#3E1F46] hover:bg-[#2C1531] text-white text-xs tracking-[0.2em] uppercase font-semibold"><Save className="w-4 h-4" /> Save</button>
            </div>
            <style>{`.ji{width:100%;padding:8px 12px;border:1px solid #E8DFCB;border-radius:6px;background:white;font-size:14px;font-family:inherit;color:#3C2A1E;}.ji:focus{outline:none;border-color:#3E1F46;}textarea.ji{min-height:60px;}`}</style>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.3em] uppercase text-[#9C8B73] font-semibold mb-1">{label}</span>
      {children}
    </label>
  );
}
